/* =========================================================
   Jungle Powders: Fast Bundle Mix&Match auto-add (NO LOOP)
   - Auto-add current PDP product ONCE per modal open
   - Never adds again if product already appears in "Added items"
   - Portal-safe (cards may be outside [role="dialog"])
   ========================================================= */

(() => {
  const LOG = "[JP FastBundle AutoAdd]";
  const DEBUG = true;

  const log = (...a) => DEBUG && console.log(LOG, ...a);
  const warn = (...a) => console.warn(LOG, ...a);
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  // ------------------ PDP helpers ------------------
  function getPdpHandle() {
    const parts = location.pathname.split("/products/");
    return parts[1] ? parts[1].split(/[?#]/)[0] : null;
  }

  function getPdpSelectedVariantId() {
    const input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    const v = input?.value;
    return v && /^\d+$/.test(v) ? v : null;
  }

  // ------------------ Modal state ------------------
  function isBundleModalOpen() {
    // Fast Bundle modal wrappers vary; these are common in your HTML
    return !!(
      document.querySelector('[class*="modal_modal_body_"]') ||
      document.querySelector('[class*="pMMBundleBuilderPage_container_"]') ||
      document.querySelector('[class*="cartInfo_card_"]')
    );
  }

  // Find builder root anywhere (portal-safe)
  function getBuilderRoot() {
    return (
      document.querySelector('[class*="pMMBundleBuilderPage_container_"]') ||
      document.querySelector('[class*="productsCard_products_container_"]') ||
      document.querySelector('[class*="style_collectionItems_"]')?.closest("div") ||
      null
    );
  }

  // Find the “Added items” panel root
  function getAddedItemsRoot() {
    return document.querySelector('[class*="cartInfo_card_added_items_"]')
      || document.querySelector('[class*="cartInfo_card_"]');
  }

  // Guard: is current product already added?
  function isAlreadyAdded(handle) {
    if (!handle) return false;
    const addedRoot = getAddedItemsRoot();
    if (!addedRoot) return false;

    // Look for a matching /products/handle link anywhere inside the added area
    const links = [...addedRoot.querySelectorAll('a[href*="/products/"]')]
      .map(a => a.getAttribute("href") || "");

    if (links.some(h => h.includes(`/products/${handle}`))) return true;

    // Fallback: sometimes added list shows title without link; try handle-ish text
    const txt = (addedRoot.textContent || "").toLowerCase();
    if (txt.includes(handle.replace(/-/g, " "))) return true;

    return false;
  }

  // ------------------ Card finding ------------------
  function findCardByHandle(root, handle) {
    if (!root || !handle) return null;
    const cards = [...root.querySelectorAll('[class*="mamProductItemCard_container_"]')];
    for (const c of cards) {
      const a = c.querySelector('a[href*="/products/"]');
      const href = a?.getAttribute("href") || "";
      if (href.includes(`/products/${handle}`)) return c;
    }
    return null;
  }

  function getScrollContainer(root) {
    if (!root) return null;

    const candidates = [
      root.querySelector('[class*="style_collectionItems_"]'),
      root.querySelector('[class*="productsCard_products_container_"]'),
      root,
    ].filter(Boolean);

    const isScrollable = (el) => el.scrollHeight > el.clientHeight + 5;

    for (const el of candidates) if (isScrollable(el)) return el;

    // walk up just in case
    let p = root.parentElement;
    for (let i = 0; i < 6 && p; i++) {
      if (isScrollable(p)) return p;
      p = p.parentElement;
    }
    return null;
  }

  // ------------------ Click add ------------------
  function clickAddOnCard(card, pdpVariantId) {
    if (!card) return false;

    const btn = card.querySelector('button[class*="variantSelector_container_"]');
    if (btn) {
      btn.click();
      log("Clicked Add button ✅");
      return true;
    }

    const sel = card.querySelector('select[class*="variantSelector_container_"]');
    if (sel) {
      if (pdpVariantId) {
        const opt = [...sel.options].find(o => o.value === String(pdpVariantId) && !o.disabled);
        if (opt) {
          sel.value = opt.value;
          sel.dispatchEvent(new Event("change", { bubbles: true }));
          log("Selected matching variant ✅", pdpVariantId);
          return true;
        }
      }

      const fallback = [...sel.options].find(
        o => o.value && o.value !== "add" && !o.disabled && !o.hidden
      );
      if (fallback) {
        sel.value = fallback.value;
        sel.dispatchEvent(new Event("change", { bubbles: true }));
        log("Selected fallback variant ✅", fallback.value);
        return true;
      }
    }

    return false;
  }

  async function scrollFindAndAdd(root, handle, pdpVariantId) {
    const scroller = getScrollContainer(root);
    if (!scroller) return false;

    try { scroller.scrollTop = 0; } catch (_) {}

    const STEP_MS = 250;
    const STEP_PX = Math.max(260, Math.floor(scroller.clientHeight * 0.85));
    const MAX_STEPS = 60;

    for (let i = 0; i < MAX_STEPS; i++) {
      const card = findCardByHandle(root, handle);
      if (card) return clickAddOnCard(card, pdpVariantId);

      const before = scroller.scrollTop;
      scroller.scrollTop = Math.min(scroller.scrollTop + STEP_PX, scroller.scrollHeight);
      await sleep(STEP_MS);

      if (scroller.scrollTop === before) break;
    }

    const last = findCardByHandle(root, handle);
    if (last) return clickAddOnCard(last, pdpVariantId);
    return false;
  }

  // ------------------ Run-once-per-open logic ------------------
  // We treat “open” as: modal became open, then later modal closed resets the key.
  let ranForThisOpenKey = null;
  let running = false;

  function currentOpenKey() {
    const handle = getPdpHandle();
    return handle ? `open::${handle}` : null;
  }

  function resetWhenClosed() {
    if (!isBundleModalOpen()) {
      ranForThisOpenKey = null;
      running = false;
    }
  }

  async function tryAutoAddOnce() {
    if (running) return;
    if (!isBundleModalOpen()) return;

    const handle = getPdpHandle();
    if (!handle) return;

    // Hard guard 1: already added => never click
    if (isAlreadyAdded(handle)) {
      log("Already added — skipping ✅", handle);
      return;
    }

    const key = currentOpenKey();

    // Hard guard 2: only run once per modal open
    if (ranForThisOpenKey === key) return;

    running = true;
    ranForThisOpenKey = key;

    const pdpVariantId = getPdpSelectedVariantId();
    log("Auto-add start", { handle, pdpVariantId });

    const TIMEOUT_MS = 10000;
    const start = Date.now();

    while (Date.now() - start < TIMEOUT_MS) {
      if (!isBundleModalOpen()) break;

      // if it becomes already added during attempts, stop
      if (isAlreadyAdded(handle)) {
        log("Detected added during run — stop ✅");
        running = false;
        return;
      }

      const root = getBuilderRoot();
      if (root) {
        const ok = await scrollFindAndAdd(root, handle, pdpVariantId);
        if (ok) {
          // After clicking, wait a moment and confirm added state to prevent loops
          await sleep(600);
          if (isAlreadyAdded(handle)) log("Confirmed added ✅");
          running = false;
          return;
        }
      }

      await sleep(300);
    }

    warn("Auto-add failed (not found).", { handle, pdpVariantId });
    running = false;
  }

  // ------------------ Observers (throttled) ------------------
  let scheduled = false;
  function scheduleTry(delay = 450) {
    if (scheduled) return;
    scheduled = true;
    setTimeout(() => {
      scheduled = false;
      resetWhenClosed();
      tryAutoAddOnce();
    }, delay);
  }

  const mo = new MutationObserver(() => {
    if (isBundleModalOpen()) scheduleTry(450);
    else resetWhenClosed();
  });

  mo.observe(document.documentElement, { childList: true, subtree: true });

  // Chip click triggers it too
  document.addEventListener("click", (e) => {
    const chip = e.target.closest("[data-jp-fastbundle-trigger], .jp-bundle-chip");
    if (!chip) return;
    scheduleTry(650);
  });

  log("Loaded ✅ (no-loop)");
})();